import { clearVariant, getVariant } from "../utils/fieldTest";
import { PLACEHOLDER_IMAGE_URN } from "../utils/placeholderImage";
import { makeImgUrl, setQueryParams } from "../utils/URL";
const experiment = {
    name: "HideProductPhotos",
    variants: [
        ["Show", 1],
        ["Hide", 1],
    ],
};
const variant = (() => {
    if (Skubacz.configuration.hide_photos_experiment_enabled) {
        return getVariant(experiment);
    }
    else {
        clearVariant(experiment);
        return "Show";
    }
})();
/**
 * "Hide Photos AB test" is meant to test the impact of adding AI-generated photos to products.
 * Restaurants selected for the test[1] had no or little own photos. AI-generated photos were added to them.
 *
 * The experiment has to variants: `Show` and `Hide`. `Show` does nothing, `Hide` hides all photos (modifies the page as if no product had a photo added).
 * (Note: initial idea was to hide only the AI photos, but to simplify experiment code, we decided to choose restaurants with almost no photos and hide all of them.)
 *
 * Modifying the page to remove photos ain't easy. We need to influence photo display in:
 *
 * 1. Statically-rendered page content, specifically product photos in menu.
 *
 *   Different types of photo display (`dish_image_type` theme setting) have to be handled differently
 *     - icon - displays just a camera icon to show popup - we just hide it
 *     - big - we replace the image source with a placeholder
 *     - thumbnail - we replace the image source with a placeholder.
 *        Also need to disable popup onclick handler, and icon on hover (note: this is not tested by e2e test. Maybe it should?)
 *
 *   This is handled by two DOM-based mechanisms:
 *   - For `.js-dish-image` elements, we replace image path in their `<img>` children, and disable lightbox/photo popup on them
 *   - `.js-dish-image-hide` elements are hidden. This is used for e.g. camera icons.
 *
 * 2. Dynamic content (product card, cart, checkout), in two implementations (old PS, new Client).
 *   This is handled by exposing a function `filterProductPhoto`, used in the necessary places.
 *
 * [1]: https://docs.google.com/spreadsheets/d/1nztMUQlv_3aGT34BWeIXUazl6EiUw_-saJQcu7XW4AI/edit#gid=1479682484
 */
export function applyHidePhotosExperiment() {
    if (variant === "Hide") {
        hideProductImages();
    }
}
/**
 * Nullify product photo if the experiment is active.
 */
export function filterProductPhoto(photo) {
    return variant === "Hide" ? null : photo;
}
function hideProductImages() {
    for (const el of document.querySelectorAll(".js-dish-image")) {
        el.removeAttribute("href");
        el.removeAttribute("data-bs-toggle");
        el.removeAttribute("data-lightbox");
        const image = el.querySelector("img");
        if (image) {
            image.src = toPlaceholder(image.src);
            image.srcset = toPlaceholder(image.srcset);
        }
    }
    for (const el of document.querySelectorAll(".js-dish-image-hide")) {
        el.style.display = "none";
    }
}
/**
 * Change an image URL to placeholder, while retaining imgix parameters `w` and `h`.
 *
 * e.g. toPlaceholder('https://restaumatic-development.imgix.net/uploads/media_library/95a4804aeda71af65675c9a4a3ee70ab.gif?auto=compress%2Cformat&blur=0&crop=focalpoint&fit=crop&fp-x=0.5&fp-y=0.5&h=140&rect=0%2C0%2C146%2C239&w=185')
 */
function toPlaceholder(src) {
    if (!src) {
        return "";
    }
    const url = new URL(src);
    const params = {
        auto: "compress",
        crop: "focalpoint",
        fit: "crop",
    };
    for (const param of ["w", "h"]) {
        const value = url.searchParams.get(param);
        if (value) {
            params[param] = value.toString();
        }
    }
    const result = setQueryParams(params, makeImgUrl(PLACEHOLDER_IMAGE_URN));
    console.log(`Hiding image ${src} -> ${result}`);
    return result;
}
